import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useGovernance } from '../../context/GovernanceContext';
import { DIMENSIONS } from '../../data/questions';
import { formatScore } from '../../utils/scoring';
import ScoreBar from '../shared/ScoreBar';
import MaturityLabel from '../shared/MaturityLabel';
import ClientForm from './ClientForm';
import ConfirmDialog from '../shared/ConfirmDialog';

export default function ClientList() {
  const { clients, loading, deleteClient, getClientDimensionScore, getClientOverallScore } =
    useGovernance();
  const navigate = useNavigate();

  const [showForm, setShowForm] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const openAdd = () => { setEditClient(null); setShowForm(true); };
  const openEdit = (c) => { setEditClient(c); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditClient(null); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteClient(deleteTarget.id);
    setDeleteTarget(null);
  };

  if (loading) {
    return <div className="text-mid-grey text-sm">Loading clients…</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-brand-navy">Clients</h1>
          <p className="text-sm text-mid-grey mt-0.5">
            Agentic AI governance assessments — Singapore IMDA Framework 2026
          </p>
        </div>
        <button
          onClick={openAdd}
          className="bg-brand-navy text-white px-4 py-2 rounded text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          + Add client
        </button>
      </div>

      {clients.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="text-4xl mb-3">🏢</div>
          <p className="text-brand-navy font-semibold">No clients yet</p>
          <p className="text-mid-grey text-sm mt-1">Add your first client to start an assessment.</p>
          <button
            onClick={openAdd}
            className="mt-4 bg-brand-navy text-white px-4 py-2 rounded text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            Add client
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-light-grey">
                <th className="text-left px-5 py-3 text-xs font-semibold text-mid-grey uppercase tracking-wide">
                  Client
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-mid-grey uppercase tracking-wide hidden md:table-cell">
                  Industry
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-mid-grey uppercase tracking-wide">
                  Overall
                </th>
                {DIMENSIONS.map((d) => (
                  <th
                    key={d.num}
                    className="text-center px-3 py-3 text-xs font-semibold text-mid-grey uppercase tracking-wide hidden lg:table-cell"
                  >
                    D{d.num}
                  </th>
                ))}
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => {
                const overall = getClientOverallScore(client.id);
                return (
                  <tr
                    key={client.id}
                    className="border-b border-gray-100 last:border-0 hover:bg-light-grey/50 transition-colors"
                  >
                    {/* Name */}
                    <td className="px-5 py-4">
                      <div className="font-semibold text-brand-navy">{client.name}</div>
                      {client.contact_name && (
                        <div className="text-xs text-mid-grey mt-0.5">{client.contact_name}</div>
                      )}
                    </td>

                    {/* Industry */}
                    <td className="px-5 py-4 hidden md:table-cell">
                      {client.industry ? (
                        <span className="inline-block bg-blue-50 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full">
                          {client.industry}
                        </span>
                      ) : (
                        <span className="text-mid-grey">—</span>
                      )}
                    </td>

                    {/* Overall score */}
                    <td className="px-5 py-4">
                      <div className="flex flex-col gap-1 min-w-[120px]">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-brand-navy">
                            {formatScore(overall)}
                          </span>
                          <MaturityLabel score={overall} size="sm" />
                        </div>
                        <ScoreBar score={overall} />
                      </div>
                    </td>

                    {/* Per-dimension scores */}
                    {DIMENSIONS.map((d) => {
                      const s = getClientDimensionScore(client.id, d.num);
                      return (
                        <td key={d.num} className="px-3 py-4 text-center hidden lg:table-cell">
                          <span
                            className="inline-block text-xs font-bold px-2 py-1 rounded-full"
                            style={{
                              backgroundColor: s ? `${d.color}18` : '#F3F4F6',
                              color: s ? d.color : '#9CA3AF',
                            }}
                          >
                            {formatScore(s)}
                          </span>
                        </td>
                      );
                    })}

                    {/* Actions */}
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => navigate(`/clients/${client.id}`)}
                          className="text-xs font-semibold text-brand-teal hover:underline"
                        >
                          View
                        </button>
                        <Link
                          to={`/clients/${client.id}/intake`}
                          className="text-xs font-semibold text-purple-600 hover:text-purple-800"
                        >
                          Intake
                        </Link>
                        <button
                          onClick={() => openEdit(client)}
                          className="text-xs font-semibold text-mid-grey hover:text-brand-navy"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteTarget(client)}
                          className="text-xs font-semibold text-red-400 hover:text-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ClientForm isOpen={showForm} onClose={closeForm} client={editClient} />

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete client"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? All assessments and actions for this client will be permanently removed.`}
        confirmLabel="Delete client"
        danger
      />
    </div>
  );
}
